import { useCallback, useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PageHeader, Loading } from "../../components/shared";
import { RBAC_ACTIONS, RBAC_MODULES, RBAC_SCOPES, blankPermissions, permissionCount } from "../../lib/rbac";
import { toast } from "sonner";

const emptyRole = () => ({
  name: "",
  description: "",
  status: "Active",
  scope: "ALL",
  permissions: blankPermissions(),
});

export default function AdminRoles() {
  const [roles, setRoles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRole());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/admin/roles");
      setRoles(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load roles");
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyRole());
    setDialogOpen(true);
  };
  const openEdit = (role) => {
    setEditing(role);
    setForm({
      name: role.name || "",
      description: role.description || "",
      status: role.status || "Active",
      scope: role.scope || "ALL",
      permissions: { ...blankPermissions(), ...(role.permissions || {}) },
    });
    setDialogOpen(true);
  };
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setPermission = (module, action, checked) => {
    setForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [module]: { ...current.permissions[module], [action]: Boolean(checked) },
      },
    }));
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/roles/${editing.rbac_role_id}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/admin/roles", form);
        toast.success("Role created");
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save role");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (role) => {
    if (!window.confirm(`Delete ${role.name}?`)) return;
    try {
      await api.delete(`/admin/roles/${role.rbac_role_id}`);
      toast.success("Role deleted");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete role");
    }
  };

  if (!roles) return <Loading />;

  return (
    <div data-testid="admin-roles-page">
      <PageHeader
        title="Roles & Permissions"
        subtitle="Set exact module actions and the data scope that each internal role can access."
      >
        <Button className="rounded-full gap-2" data-testid="role-create-button" onClick={openCreate}>
          <Plus className="w-4 h-4" />
          Add Role
        </Button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="role-list">
        {roles.map((role) => (
          <article
            key={role.rbac_role_id}
            className="bg-card border rounded-xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
            data-testid={`role-card-${role.rbac_role_id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold" data-testid={`role-name-${role.rbac_role_id}`}>
                    {role.name}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1" data-testid={`role-scope-${role.rbac_role_id}`}>
                    {RBAC_SCOPES.find(([value]) => value === role.scope)?.[1] || "All platform data"}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  aria-label={`Edit ${role.name}`}
                  data-testid={`role-edit-${role.rbac_role_id}`}
                  onClick={() => openEdit(role)}
                  size="icon"
                  variant="ghost"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  aria-label={`Delete ${role.name}`}
                  className="text-destructive"
                  data-testid={`role-delete-${role.rbac_role_id}`}
                  onClick={() => remove(role)}
                  size="icon"
                  variant="ghost"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-5 min-h-10">
              {role.description || "No internal description added."}
            </p>
            <div className="flex items-center justify-between border-t mt-5 pt-3 text-sm">
              <span className="text-muted-foreground">Allowed actions</span>
              <strong data-testid={`role-permission-count-${role.rbac_role_id}`}>
                {permissionCount(role.permissions)}
              </strong>
            </div>
          </article>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="border border-dashed rounded-xl p-12 text-center text-muted-foreground" data-testid="role-empty-state">
          No custom roles yet. Create one before adding internal staff.
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Role" : "Add Role"}</DialogTitle>
            <DialogDescription>
              Permissions are enforced in both the staff menu and protected platform APIs.
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-[1fr_180px_150px] gap-4">
            <div>
              <Label htmlFor="role-name">Role name</Label>
              <Input
                data-testid="role-name-input"
                id="role-name"
                onChange={(event) => setField("name", event.target.value)}
                value={form.name}
              />
            </div>
            <div>
              <Label htmlFor="role-scope">Data access scope</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="role-scope-select"
                id="role-scope"
                onChange={(event) => setField("scope", event.target.value)}
                value={form.scope}
              >
                {RBAC_SCOPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="role-status">Status</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="role-status-select"
                id="role-status"
                onChange={(event) => setField("status", event.target.value)}
                value={form.status}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="role-description">Internal description</Label>
            <Input
              data-testid="role-description-input"
              id="role-description"
              onChange={(event) => setField("description", event.target.value)}
              value={form.description}
            />
          </div>
          <PermissionMatrix permissions={form.permissions} setPermission={setPermission} />
          <DialogFooter>
            <Button data-testid="role-save-button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : editing ? "Save Changes" : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionMatrix({ permissions, setPermission }) {
  return (
    <div className="border rounded-xl overflow-x-auto" data-testid="role-permission-matrix">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            <th className="p-3 font-semibold">Platform module</th>
            {RBAC_ACTIONS.map((action) => (
              <th className="p-3 font-semibold capitalize text-center" key={action}>{action}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RBAC_MODULES.map(([module, label]) => (
            <tr className="border-t" key={module}>
              <td className="p-3 font-medium">{label}</td>
              {RBAC_ACTIONS.map((action) => (
                <td className="p-3 text-center" key={action}>
                  <Checkbox
                    aria-label={`${label}: ${action}`}
                    checked={Boolean(permissions[module]?.[action])}
                    data-testid={`role-permission-${module}-${action}`}
                    onCheckedChange={(checked) => setPermission(module, action, checked)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}